import React from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import Layout from './components/Layout'
import Login from './components/Login'
import IndexRedirect from './components/shared/IndexRedirect'
import MobilePhotoUpload from './components/warehouse/MobilePhotoUpload'

// Domain pages
import Dashboard from './components/Dashboard'
import Activities from './components/Activities'
import Tickets from './components/Tickets'
import RecordsPanel from './components/records/RecordsPanel'

// Deals
import DealsPage from './components/deals/DealsPage'
import QuoteRequested from './components/deals/QuoteRequested'
import QuoteAssessment from './components/deals/QuoteAssessment'
import AllDeals from './components/deals/AllDeals'
import DealSearch from './components/deals/DealSearch'
import DealCollections from './components/deals/Collections'
import Payments from './components/deals/Payments'
import Consignments from './components/deals/Consignments'
import CollectionExceptions from './components/deals/CollectionExceptions'
import Remainders from './components/deals/Remainders'
import WarehouseDropOff from './components/deals/WarehouseDropOff'
import CreateDeal from './components/deals/CreateDeal'
import PartExPage from './components/deals/PartExPage'
import PartExDeals from './components/deals/PartExDeals'
import AudioRaider from './components/deals/AudioRaider'

// Customers
import Customers from './components/customers/Customers'
import CustomerDetail from './components/customers/CustomerDetail'

// Inventory
import InventoryPage from './components/inventory/InventoryPage'
import DraftsManager from './components/inventory/DraftsManager'
import ActivationHistory from './components/inventory/ActivationHistory'
import InventoryList from './components/inventory/InventoryList'
import StockCheck from './components/inventory/StockCheck'
import ProductDeepLink from './components/inventory/ProductDeepLink'
import PrintingSettings from './components/inventory/PrintingSettings'
import Collections from './components/inventory/Collections'
import MarketingPage from './components/marketing/MarketingPage'
import StaffPicks from './components/marketing/StaffPicks'
import NotifyMeProducts from './components/marketing/NotifyMeProducts'
import AbandonedTradeForms from './components/marketing/AbandonedTradeForms'
import AccountDeletions from './components/marketing/AccountDeletions'
import TradeBoost from './components/marketing/TradeBoost'
import Referrals from './components/marketing/Referrals'
import ReferralPortal from './components/marketing/ReferralPortal'

// Pricing
import PricingPage from './components/pricing/PricingPage'
import PricingRules from './components/pricing/PricingRules'
import PricingAuditLog from './components/pricing/PricingAuditLog'
import PricingManagement from './components/pricing/PricingManagement'
import PricingCalendar from './components/pricing/PricingCalendar'

// Orders
import OrdersPage from './components/orders/OrdersPage'
import PrintrunsTab from './components/orders/PrintrunsTab'
import OrdersList from './components/orders/OrdersList'
import CasesTab from './components/orders/CasesTab'
import ReverbOffersTab from './components/orders/ReverbOffersTab'
import ReturnItemsTab from './components/orders/ReturnItemsTab'

// Warehouse
import WarehousePage from './components/warehouse/WarehousePage'
import WarehouseHome from './components/warehouse/WarehouseHome'
import Receiving from './components/warehouse/Receiving'
import Testing from './components/warehouse/Testing'
import PartiallyTested from './components/warehouse/PartiallyTested'
import Repairs from './components/warehouse/Repairs'
import IssueTrades from './components/warehouse/IssueTrades'
import ToSendBack from './components/warehouse/ToSendBack'
import Putaway from './components/warehouse/Putaway'
import NoLocationReport from './components/warehouse/NoLocationReport'
import Photography from './components/warehouse/Photography'
import OrderPicking from './components/warehouse/OrderPicking'
import OrderShipping from './components/warehouse/OrderShipping'
import ManualListing from './components/warehouse/ManualListing'

// Catalog (KG review surface)
import CatalogPage from './components/catalog/CatalogPage'
import CatalogReview from './components/catalog/CatalogReview'
import CatalogProposals from './components/catalog/CatalogProposals'
import CatalogSettings from './components/catalog/CatalogSettings'
import CatalogIdentify from './components/catalog/CatalogIdentify'
import CatalogLabel from './components/catalog/CatalogLabel'

// PIM
import PIMPage from './components/pim/PIMPage'
import PIMProducts from './components/pim/PIMProducts'
import MasterProductDetail from './components/pim/MasterProductDetail'
import VariantDetail from './components/pim/VariantDetail'
import PIMBrands from './components/pim/PIMBrands'
import BrandDetail from './components/pim/BrandDetail'
import PIMCategories from './components/pim/PIMCategories'
import PIMDimensions from './components/pim/PIMDimensions'
import DimensionTypeDetail from './components/pim/DimensionTypeDetail'
import PIMReview from './components/pim/PIMReview'

// Admin
import AdminPanel from './components/admin/AdminPanel'
import ClickyText from './components/admin/ClickyText'

// Handheld (Zebra PDA)
import PdaShell from './components/pda/PdaShell'
import PdaPutaway from './components/pda/PdaPutaway'
import PdaLookup from './components/pda/PdaLookup'
import PdaConsolidate from './components/pda/PdaConsolidate'

// Testing
import PaymentConfirmationEmail from './components/testing/PaymentConfirmationEmail'
import QuoteDummy from './components/testing/QuoteDummy'
import MobileQuoteUpload from './components/testing/MobileQuoteUpload'

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />
  }

  return children
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!user?.is_admin) {
    return <Navigate to="/" replace />
  }

  return children
}

// Admin > Clicky Text is open to every logged-in user. Admins get the full
// Admin panel on this tab; everyone else sees only the Clicky Text tab.
function ClickyTextRoute() {
  const { user } = useAuth()
  if (user?.is_admin) return <AdminPanel />
  return (
    <div className="admin-panel">
      <div className="admin-tabs">
        <button className="admin-tab active">Clicky Text</button>
      </div>
      <ClickyText />
    </div>
  )
}

// A section open to holders of one permission. Admins pass (hasPermission).
function PermissionRoute({ permission, children }) {
  const { loading, hasPermission } = useAuth()

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!hasPermission(permission)) {
    return <Navigate to="/" replace />
  }

  return children
}

// Permission key → route path mapping for non-admin default landing page
const PERMISSION_ROUTES = [
  ['warehouse', '/warehouse'],
  ['deals', '/deals'],
  ['inventory', '/inventory'],
  ['orders', '/orders'],
  ['tickets', '/tickets'],
  ['pim', '/catalog'],
]

function DefaultRoute() {
  const { user, hasPermission } = useAuth()

  if (user?.is_admin) {
    return <Dashboard />
  }

  // Redirect non-admins to their first permitted page
  for (const [perm, path] of PERMISSION_ROUTES) {
    if (hasPermission(perm)) {
      return <Navigate to={path} replace />
    }
  }

  // Fallback: no permissions at all
  return <Dashboard />
}

// /catalog/review is admin-only, so the catalog index lands non-admins on
// Proposals — bouncing them off AdminRoute to "/" would loop back here via
// DefaultRoute's pim → /catalog mapping.
function CatalogIndexRedirect() {
  const { user } = useAuth()
  return <Navigate to={user?.is_admin ? 'review' : 'proposals'} replace />
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/mobile-photo-upload/:token" element={<MobilePhotoUpload />} />
        <Route path="/mobile-quote-upload/:token" element={<MobileQuoteUpload />} />
        {/* Public referrer portal - no login, no app shell (needs the :slug segment;
            the admin /marketing/referrals page matches the slug-less path instead) */}
        <Route path="/marketing/referrals/:slug" element={<ReferralPortal />} />

        {/* Handheld scanner screens. Outside the app shell on purpose: the
            sidebar and mobile header together eat most of a 4" panel, and the
            operator drives these with a trigger rather than a thumb. */}
        <Route path="/pda" element={
          <ProtectedRoute>
            <PdaShell />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="putaway" replace />} />
          <Route path="putaway" element={<PdaPutaway />} />
          <Route path="lookup" element={<PdaLookup />} />
          <Route path="consolidate" element={<PdaConsolidate />} />
        </Route>

        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          {/* Dashboard (admin) or redirect to first permitted page */}
          <Route index element={<DefaultRoute />} />
          {/* Short QR deep link printed on warehouse labels */}
          <Route path="i/:productId" element={<ProductDeepLink />} />
          <Route path="activities" element={<Activities />} />
          <Route path="tickets" element={<Tickets />} />
          <Route path="records" element={<RecordsPanel />} />

          {/* Customers — one record per person, and every deal they have had */}
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:customerId" element={<CustomerDetail />} />

          {/* Deals */}
          <Route path="deals" element={<DealsPage />}>
            <Route index element={<Navigate to="all" replace />} />
            <Route path="search" element={<DealSearch />} />
            <Route path="quote-requested" element={<QuoteRequested />} />
            <Route path="quote-assessment" element={<QuoteAssessment />} />
            <Route path="all" element={<AllDeals />} />
            <Route path="audio-raider" element={<AudioRaider />} />
            <Route path="create" element={<CreateDeal />} />
            <Route path="collections" element={<DealCollections />} />
            <Route path="warehousedropoff" element={<WarehouseDropOff />} />
            <Route path="payments" element={<Payments />} />
            <Route path="consignments" element={<Consignments />} />
            <Route path="part-ex" element={<PartExPage />}>
              <Route index element={<Navigate to="quote-requested" replace />} />
              <Route path="quote-requested" element={<PartExDeals status="quote_requested" />} />
              <Route path="quote-sent" element={<PartExDeals status="quote_sent" />} />
              <Route path="awaiting-collection" element={<PartExDeals status="awaiting_shipping_label" />} />
              <Route path="shipping-arranged" element={<PartExDeals status="shipping_arranged" />} />
              <Route path="received" element={<PartExDeals status="received" />} />
              <Route path="accepted" element={<PartExDeals status="accepted" />} />
              <Route path="expired" element={<PartExDeals status="expired" />} />
            </Route>
            <Route path="exceptions" element={<CollectionExceptions />} />
            <Route path="remainders" element={<Remainders />} />
          </Route>

          {/* Inventory */}
          <Route path="inventory" element={<InventoryPage />}>
            <Route index element={<Navigate to="list" replace />} />
            <Route path="drafts" element={<DraftsManager />} />
            <Route path="activation-history" element={<ActivationHistory />} />
            <Route path="list" element={<InventoryList />} />
            <Route path="stock-check" element={<StockCheck />} />
            <Route path="collections" element={<Collections />} />
            <Route path="printing" element={<PrintingSettings />} />
          </Route>

          {/* Pricing: pricing:view to read; every write is gated on pricing:write */}
          <Route path="pricing" element={<PermissionRoute permission="pricing:view"><PricingPage /></PermissionRoute>}>
            <Route index element={<Navigate to="management" replace />} />
            <Route path="management" element={<PricingManagement />} />
            <Route path="calendar" element={<PricingCalendar />} />
            <Route path="rules" element={<PricingRules />} />
            <Route path="audit-log" element={<PricingAuditLog />} />
          </Route>

          {/* Marketing */}
          <Route path="marketing" element={<MarketingPage />}>
            <Route index element={<Navigate to="staff-picks" replace />} />
            <Route path="staff-picks" element={<StaffPicks />} />
            <Route path="notify-me" element={<NotifyMeProducts />} />
            <Route path="abandoned-trade-forms" element={<AbandonedTradeForms />} />
            <Route path="account-deletions" element={<AdminRoute><AccountDeletions /></AdminRoute>} />
            <Route path="trade-boost" element={<AdminRoute><TradeBoost /></AdminRoute>} />
            <Route path="referrals" element={<AdminRoute><Referrals /></AdminRoute>} />
          </Route>

          {/* Orders */}
          <Route path="orders" element={<OrdersPage />}>
            <Route index element={<IndexRedirect to="list" />} />
            <Route path="list" element={<OrdersList />} />
            <Route path="cases" element={<CasesTab />} />
            <Route path="reverb-offers" element={<ReverbOffersTab />} />
            <Route path="returns" element={<ReturnItemsTab />} />
            <Route path="printruns" element={<PrintrunsTab />} />
          </Route>

          {/* Warehouse */}
          <Route path="warehouse" element={<WarehousePage />}>
            <Route index element={<WarehouseHome />} />
            <Route path="receiving" element={<Receiving />} />
            <Route path="testing" element={<Testing />} />
            <Route path="partially-tested" element={<PartiallyTested />} />
            <Route path="repairs" element={<Repairs />} />
            <Route path="issue-trades" element={<IssueTrades />} />
            <Route path="to-send-back" element={<ToSendBack />} />
            <Route path="putaway" element={<Putaway />} />
            <Route path="no-location" element={<NoLocationReport />} />
            <Route path="photography" element={<Photography />} />
            <Route path="order-picking" element={<OrderPicking />} />
            <Route path="shipping" element={<OrderShipping />} />
            <Route path="manual-listing" element={<ManualListing />} />
          </Route>

          {/* Catalog (KG review surface — successor to the PIM screens).
              Review Queue is admin-only; Proposals/Settings stay on pim. */}
          <Route path="catalog" element={<CatalogPage />}>
            <Route index element={<CatalogIndexRedirect />} />
            {/* Browse is read-only for any authenticated staff (F8); mutations
                and the review queue stay admin-gated at the worker + their routes. */}
            <Route path="products" element={<ProtectedRoute><CatalogReview mode="browse" /></ProtectedRoute>} />
            <Route path="review" element={<AdminRoute><CatalogReview /></AdminRoute>} />
            <Route path="identify" element={<AdminRoute><CatalogIdentify /></AdminRoute>} />
            <Route path="label" element={<AdminRoute><CatalogLabel /></AdminRoute>} />
            <Route path="proposals" element={<CatalogProposals />} />
            <Route path="settings" element={<CatalogSettings />} />
          </Route>

          {/* PIM (legacy — off the menu since the Catalog section landed;
              routes stay URL-reachable until the staged teardown) */}
          <Route path="pim" element={<PIMPage />}>
            <Route index element={<Navigate to="products" replace />} />
            <Route path="products" element={<PIMProducts />} />
            <Route path="products/:id" element={<MasterProductDetail />} />
            <Route path="variants/:id" element={<VariantDetail />} />
            <Route path="brands" element={<PIMBrands />} />
            <Route path="brands/:id" element={<BrandDetail />} />
            <Route path="categories" element={<PIMCategories />} />
            <Route path="dimensions" element={<PIMDimensions />} />
            <Route path="dimensions/:id" element={<DimensionTypeDetail />} />
            <Route path="review" element={<PIMReview />} />
          </Route>

          {/* Admin > Clicky Text: every logged-in user (matches before the admin-only block) */}
          <Route path="admin/clicky-text" element={<ClickyTextRoute />} />

          {/* Admin — tabs are URL-addressable under /admin/<tab> */}
          <Route path="admin" element={<AdminRoute><AdminPanel /></AdminRoute>}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<AdminPanel />} />
            <Route path="technicians" element={<AdminPanel />} />
            <Route path="settings" element={<AdminPanel />} />
            <Route path="video-assess" element={<AdminPanel />} />
            <Route path="title-cleanup" element={<AdminPanel />} />
            <Route path="shelf-manager" element={<AdminPanel />} />
            <Route path="warehouse-capacity" element={<AdminPanel />} />
            <Route path="environment-config" element={<AdminPanel />} />
          </Route>

          {/* Testing (admin only) */}
          <Route path="testing/payment-email" element={
            <AdminRoute><PaymentConfirmationEmail /></AdminRoute>
          } />
          <Route path="testing/quote-dummy" element={
            <AdminRoute><QuoteDummy /></AdminRoute>
          } />
        </Route>
      </Routes>
    </AuthProvider>
  )
}

export default App
